import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import {
  ArrowRight,
  CalendarCheck,
  Check,
  ClipboardList,
  FlaskConical,
  GraduationCap,
  Layers,
  Minus,
  TrendingUp,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { BrandWordmark } from "@/components/app/brand-wordmark";
import { HomeVideo } from "./home-video";
import { FaqAccordion } from "./faq-accordion";
import { homepageContent, internalLinkMap, type LinkKey } from "@/lib/homepage-content";
import { PromoCarousel } from "./promo-carousel";
import { MarketingPage, StartFreeButton } from "./marketing-kit";

const C = homepageContent;

const TRACKER_ICONS: Record<string, LucideIcon> = {
  layers: Layers,
  graduation: GraduationCap,
  calendar: CalendarCheck,
  clipboard: ClipboardList,
  flask: FlaskConical,
  trending: TrendingUp,
};

function href(key: string) {
  return internalLinkMap[key as LinkKey] ?? "/";
}


function Eyebrow({ children }: { children: string }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
      {children}
    </p>
  );
}

function Section({
  id,
  eyebrow,
  heading,
  children,
}: {
  id?: string;
  eyebrow?: string;
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <motion.section
      id={id}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ type: "spring", stiffness: 220, damping: 28 }}
      className="mt-14 scroll-mt-16"
    >
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <h2 className="mt-2 text-[22px] font-extrabold tracking-tight lg:text-[28px]">{heading}</h2>
      <div className="mt-4">{children}</div>
    </motion.section>
  );
}

function InlineLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--primary-glow)] transition-transform hover:translate-x-0.5"
    >
      {label}
      <ArrowRight className="size-3.5" aria-hidden />
    </Link>
  );
}


export function MarketingHome() {
  return (
    <MarketingPage>
      <div className="flex justify-center">
        <BrandWordmark />
      </div>

      {/* Hero */}
      <header className="mt-8 text-center">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {C.hero.eyebrow}
        </p>
        <h1 className="mx-auto mt-2 max-w-3xl text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">{C.hero.h1}</h1>
        <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
          {C.hero.description}
        </p>
        <p className="mx-auto mt-3 max-w-2xl text-[14px] leading-relaxed text-muted-foreground">
          {C.hero.secondaryDescription}
        </p>
        <p className="mt-3 text-[13px] font-medium">{C.hero.examLine}</p>

        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <StartFreeButton label={C.hero.primaryCTA.text} />
          <a
            href="#features"
            className="glass inline-flex items-center justify-center rounded-full border border-hairline px-6 py-3 text-sm font-semibold transition-transform active:scale-95"
          >
            {C.hero.secondaryCTA.text}
          </a>
        </div>
        <p className="mt-3 text-[13px] font-semibold">{C.hero.valueLine}</p>
        <p className="mt-2 text-xs text-muted-foreground">{C.hero.trustLine}</p>
      </header>

      {/* Tracker positioning — the product's core job, stated plainly */}
      <Section eyebrow={C.trackerOverview.eyebrow} heading={C.trackerOverview.h2}>
        <div className="max-w-3xl space-y-3">
          {C.trackerOverview.paragraphs.map((p, i) => (
            <p key={p} className="text-sm leading-relaxed text-muted-foreground">
              {i === 1 ? (
                <>
                  The Quantum Minds{" "}
                  <Link to={internalLinkMap.jeeTracker} className="font-bold text-foreground">
                    JEE Tracker
                  </Link>{" "}
                  {p.replace("The Quantum Minds JEE Tracker ", "")}
                </>
              ) : (
                p
              )}
            </p>
          ))}
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {C.trackerOverview.cards.map((card) => {
            const Icon = TRACKER_ICONS[card.icon] ?? Layers;
            return (
              <motion.div
                key={card.title}
                whileHover={{ y: -3 }}
                transition={{ type: "spring", stiffness: 300, damping: 24 }}
                className="glass flex flex-col rounded-[26px] border-hairline p-5"
              >
                <span className="grid size-9 place-items-center rounded-2xl bg-surface">
                  <Icon className="size-4 text-primary" aria-hidden />
                </span>
                <h3 className="mt-3 text-[15px] font-bold tracking-tight">{card.title}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                  {card.description}
                </p>
                <InlineLink to={href(card.linkKey)} label={card.linkLabel} />
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* Definition section for students searching "what is a JEE tracker" */}
      <Section eyebrow={C.whatIsTracker.eyebrow} heading={C.whatIsTracker.h2}>
        <div className="max-w-3xl space-y-3">
          {C.whatIsTracker.paragraphs.map((p) => (
            <p key={p} className="text-sm leading-relaxed text-muted-foreground">
              {p}
            </p>
          ))}
        </div>
        <ul className="mt-5 grid gap-2.5 text-sm sm:grid-cols-2">
          {C.whatIsTracker.checklist.map((item) => (
            <li key={item} className="flex items-start gap-2.5">
              <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-surface">
                <Check className="size-3 text-primary" aria-hidden />
              </span>
              <span className="text-muted-foreground">{item}</span>
            </li>
          ))}
        </ul>
        <InlineLink
          to={href(C.whatIsTracker.ctaLinkKey)}
          label={C.whatIsTracker.ctaLabel}
        />
      </Section>

      <Section eyebrow={C.howItWorks.eyebrow} heading={C.howItWorks.h2}>
        <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
          {C.howItWorks.description}
        </p>
        <ol className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {C.howItWorks.steps.map((s) => (
            <li key={s.number} className="glass rounded-2xl border border-hairline p-4">
              <div className="flex items-baseline gap-3">
                <span className="text-[12px] font-black tabular-nums text-[var(--primary-glow)]">
                  {s.number}
                </span>
                <h3 className="text-sm font-bold uppercase tracking-[0.12em]">{s.title}</h3>
              </div>
              <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                {s.description}
              </p>
            </li>
          ))}
        </ol>
        <p className="mt-5 text-sm font-semibold">{C.howItWorks.closingLine}</p>
        <div className="mt-4">
          <StartFreeButton label={C.howItWorks.ctaLabel} />
        </div>
      </Section>

      <Section eyebrow={C.trackerBenefits.eyebrow} heading={C.trackerBenefits.h2}>
        <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
          {C.trackerBenefits.intro}
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {C.trackerBenefits.items.map((b) => (
            <div key={b.title} className="glass rounded-2xl border border-hairline p-4">
              <h3 className="text-sm font-bold tracking-tight">{b.title}</h3>
              <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                {b.description}
              </p>
            </div>
          ))}
        </div>
      </Section>

      <Section heading={C.comparison.h2}>
        <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
          {C.comparison.paragraph}
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="glass rounded-2xl border border-hairline p-4">
            <h3 className="text-sm font-bold tracking-tight">{C.comparison.todo.title}</h3>
            <ul className="mt-3 space-y-2 text-[13px] text-muted-foreground">
              {C.comparison.todo.points.map((p) => (
                <li key={p} className="flex items-start gap-2.5">
                  <Minus className="mt-0.5 size-3.5 shrink-0 opacity-60" aria-hidden />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="glass rounded-2xl border border-hairline p-4">
            <h3 className="text-sm font-bold tracking-tight">{C.comparison.tracker.title}</h3>
            <ul className="mt-3 space-y-2 text-[13px] text-muted-foreground">
              {C.comparison.tracker.points.map((p) => (
                <li key={p} className="flex items-start gap-2.5">
                  <Check className="mt-0.5 size-3.5 shrink-0 text-primary" aria-hidden />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>


      <Section eyebrow={C.whatIsIt.eyebrow} heading={C.whatIsIt.h2}>
        <div className="max-w-3xl space-y-3">
          {C.whatIsIt.paragraphs.map((p) => (
            <p key={p} className="text-sm leading-relaxed text-muted-foreground">
              {p}
            </p>
          ))}
        </div>
      </Section>

      <Section heading="See how the platform works">
        <HomeVideo />
      </Section>

      <div className="mt-14">
        <PromoCarousel />
      </div>

      <Section eyebrow={C.whyStudentsNeedIt.eyebrow} heading={C.whyStudentsNeedIt.h2}>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {C.whyStudentsNeedIt.intro}
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {C.whyStudentsNeedIt.problems.map((p) => (
            <motion.div
              key={p.problem}
              whileHover={{ y: -3 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className="glass rounded-2xl border border-hairline p-4"
            >
              <h3 className="text-sm font-bold tracking-tight">{p.problem}</h3>
              <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                {p.solution}
              </p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section eyebrow={C.preparationJourney.eyebrow} heading={C.preparationJourney.h2}>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {C.preparationJourney.description}
        </p>
        <ol className="mt-5 space-y-3">
          {C.preparationJourney.stages.map((s) => (
            <li key={s.number} className="glass rounded-2xl border border-hairline p-4">
              <div className="flex items-baseline gap-3">
                <span className="text-[12px] font-black tabular-nums text-[var(--primary-glow)]">
                  {s.number}
                </span>
                <h3 className="text-sm font-bold uppercase tracking-[0.12em]">{s.title}</h3>
              </div>
              <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                {s.description}
              </p>
              <InlineLink to={href(s.linkKey)} label={s.linkLabel} />
            </li>
          ))}
        </ol>
      </Section>

      <Section id="features" eyebrow={C.features.eyebrow} heading={C.features.h2}>
        <p className="text-sm leading-relaxed text-muted-foreground">{C.features.intro}</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {C.features.items.map((f) => (
            <motion.div
              key={f.title}
              whileHover={{ y: -3 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className="glass rounded-[26px] border-hairline p-5"
            >
              <h3 className="text-[16px] font-bold tracking-tight">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {f.description}
              </p>
              <InlineLink to={href(f.linkKey)} label={f.linkLabel} />
            </motion.div>
          ))}
        </div>
      </Section>

      <Section eyebrow={C.exams.eyebrow} heading={C.exams.h2}>
        <p className="text-sm leading-relaxed text-muted-foreground">{C.exams.intro}</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {C.exams.exams.map((e) => (
            <Link
              key={e.name}
              to={href(e.linkKey)}
              className="glass flex flex-col rounded-2xl border border-hairline px-4 py-3.5 transition-transform hover:-translate-y-0.5 active:scale-[0.98]"
            >
              <span className="text-sm font-semibold">{e.name}</span>
              <span className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                {e.description}
              </span>
            </Link>
          ))}
        </div>
      </Section>

      <Section eyebrow={C.affordability.eyebrow} heading={C.affordability.h2}>
        <div className="space-y-3">
          {C.affordability.paragraphs.map((p) => (
            <p key={p} className="text-sm leading-relaxed text-muted-foreground">
              {p}
            </p>
          ))}
        </div>
        <ul className="mt-4 space-y-2.5 text-sm">
          {C.affordability.points.map((point) => (
            <li key={point} className="flex items-start gap-2.5">
              <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-surface">
                <Check className="size-3 text-primary" aria-hidden />
              </span>
              <span className="text-muted-foreground">{point}</span>
            </li>
          ))}
        </ul>
        <div className="mt-5">
          <StartFreeButton label={C.affordability.cta} />
        </div>
      </Section>

      <Section eyebrow={C.useCases.eyebrow} heading={C.useCases.h2}>
        <div className="grid gap-3 sm:grid-cols-2">
          {C.useCases.items.map((u) => (
            <div key={u.title} className="glass rounded-2xl border border-hairline p-4">
              <h3 className="text-sm font-bold tracking-tight">{u.title}</h3>
              <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                {u.description}
              </p>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow={C.discovery.eyebrow} heading={C.discovery.h2}>
        <p className="text-sm leading-relaxed text-muted-foreground">{C.discovery.description}</p>
        <div className="mt-4 space-y-3">
          {C.discovery.journeys.map((j) => (
            <div key={j.from} className="glass rounded-2xl border border-hairline p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {j.from}
              </p>
              <p className="mt-1.5 text-[13px] leading-relaxed">{j.question}</p>
              <InlineLink to={href(j.linkKey)} label={`Explore ${j.next}`} />
            </div>
          ))}
        </div>
      </Section>

      <Section heading={C.examSelector.h2}>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {C.examSelector.description}
        </p>
        <div className="mt-4 flex flex-wrap gap-2.5">
          {C.examSelector.exams.map((e) => (
            <Link
              key={e.name}
              to={href(e.linkKey)}
              className="glass inline-flex min-h-11 items-center rounded-full border border-hairline px-4 py-2 text-xs font-semibold transition-transform active:scale-95"
            >
              {e.name}
            </Link>
          ))}
        </div>

        <h3 className="mt-8 text-[15px] font-bold tracking-tight">{C.examTrackers.h3}</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {C.examTrackers.items.map((t) => (
            <Link
              key={t.name}
              to={href(t.linkKey)}
              className="glass flex flex-col rounded-2xl border border-hairline p-4 transition-transform hover:-translate-y-0.5 active:scale-[0.98]"
            >
              <span className="text-sm font-bold tracking-tight">{t.name}</span>
              <span className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                {t.description}
              </span>
              <span className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--primary-glow)]">
                {t.cta}
                <ArrowRight className="size-3.5" aria-hidden />
              </span>
            </Link>
          ))}
        </div>
      </Section>


      <Section heading={C.faq.h2}>
        <FaqAccordion
          idPrefix="home-faq"
          items={C.faq.items.map((f) => ({ q: f.question, a: f.answer }))}
        />
      </Section>

      <div className="glass relative mt-14 overflow-hidden rounded-[28px] border-hairline p-6 text-center">
        <div
          aria-hidden
          className="neural-gradient pointer-events-none absolute -top-16 left-1/2 size-40 -translate-x-1/2 rounded-full opacity-25 blur-3xl"
        />
        <p className="relative text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {C.finalCTA.eyebrow}
        </p>
        <h2 className="relative mt-2 text-[20px] font-extrabold tracking-tight">
          {C.finalCTA.h2}
        </h2>
        <p className="relative mt-2 text-sm text-muted-foreground">{C.finalCTA.description}</p>
        <div className="relative mt-5 flex flex-wrap justify-center gap-3">
          <StartFreeButton label={C.finalCTA.primaryCTA} />
          <Link
            to={internalLinkMap.studyToolkit}
            className="glass inline-flex items-center justify-center rounded-full border border-hairline px-6 py-3 text-sm font-semibold transition-transform active:scale-95"
          >
            {C.finalCTA.secondaryCTA}
          </Link>
        </div>
      </div>

      <figure className="mt-10 text-center">
        <blockquote className="text-sm font-medium italic text-muted-foreground">
          “{C.footerQuote.quote}”
        </blockquote>
        <figcaption className="mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {C.footerQuote.brand}
        </figcaption>
      </figure>
    </MarketingPage>
  );
}
